<script lang="ts">
  import { onMount } from "svelte";
  import {
    beginHubspotOAuthLogin,
    closeHubspotAuthDialog,
    completeHubspotOAuthCallback,
    hubspotAuthDialog,
    openHubspotAuthDialog,
    refreshHubspotSessionWithToken,
  } from "../lib/hubspotAuth";

  export let enableOAuthCallback = true;

  let loading = false;
  let responseMessage = "";
  let showRefreshInput = false;
  let refreshTokenInput = "";

  const clearOAuthParams = (): void => {
    const nextUrl = `${window.location.origin}${window.location.pathname}${window.location.hash}`;
    window.history.replaceState({}, document.title, nextUrl);
  };

  const handleOAuthCallback = async (): Promise<void> => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (!code) return;

    const state = params.get("state") ?? "";
    if (state.startsWith("hubspot-card:")) {
      window.location.href =
        `${process.env.SVELTE_APP_REMOTE_URL}/api/hubspot-auth/card-callback` +
        `?code=${encodeURIComponent(code)}` +
        `&state=${encodeURIComponent(state)}`;
      return;
    }

    loading = true;
    responseMessage = "";

    const result = await completeHubspotOAuthCallback(code);
    clearOAuthParams();

    if (result.success) {
      closeHubspotAuthDialog();
    } else {
      responseMessage = result.message;
      openHubspotAuthDialog(result.message);
    }

    loading = false;
  };

  const loginWithHubSpot = async (): Promise<void> => {
    loading = true;
    responseMessage = "";

    try {
      await beginHubspotOAuthLogin();
    } catch (error: unknown) {
      responseMessage = error instanceof Error ? error.message : "Could not fetch HubSpot login URL.";
      loading = false;
    }
  };

  const submitRefreshToken = async (): Promise<void> => {
    loading = true;
    responseMessage = "";

    const result = await refreshHubspotSessionWithToken(refreshTokenInput);
    if (result.success) {
      responseMessage = "";
      closeHubspotAuthDialog();
    } else {
      responseMessage = result.message;
    }

    loading = false;
  };

  const closeDialog = (): void => {
    responseMessage = "";
    closeHubspotAuthDialog();
  };

  $: if (!$hubspotAuthDialog.open) {
    responseMessage = "";
    showRefreshInput = false;
  }

  onMount(() => {
    if (enableOAuthCallback) {
      void handleOAuthCallback();
    }
  });
</script>

{#if $hubspotAuthDialog.open}
  <div class="auth-dialog-overlay">
    <div class="auth-dialog-card">
      <div class="auth-dialog-head">
        <div>
          <p class="auth-dialog-eyebrow">HubSpot Authentication</p>
          <h2 class="auth-dialog-title">Connect HubSpot to continue</h2>
        </div>
        <button type="button" class="auth-dialog-close" on:click={closeDialog}>
          Close
        </button>
      </div>

      <p class="auth-dialog-copy">
        {$hubspotAuthDialog.message}
      </p>

      <div class="auth-dialog-highlight">
        <p class="auth-dialog-highlight-title">Continue your sync</p>
        <p class="auth-dialog-highlight-copy">
          Sign in once and you can go right back to syncing opportunities.
        </p>
      </div>

      <div class="auth-dialog-actions">
        <button
          type="button"
          class="auth-dialog-primary"
          on:click={loginWithHubSpot}
          disabled={loading}
        >
          {loading ? "Opening HubSpot..." : "Connect with HubSpot"}
        </button>

        <button
          type="button"
          class="auth-dialog-secondary"
          on:click={() => {
            showRefreshInput = !showRefreshInput;
            responseMessage = "";
          }}
        >
          {showRefreshInput ? "Hide refresh token option" : "Use refresh token"}
        </button>
      </div>

      {#if showRefreshInput}
        <div class="auth-dialog-token-box">
          <label class="auth-dialog-label" for="hubspot-refresh-token">
            Refresh token
          </label>
          <input
            id="hubspot-refresh-token"
            type="text"
            class="auth-dialog-input"
            bind:value={refreshTokenInput}
            placeholder="Paste a HubSpot refresh token"
          />
          <button
            type="button"
            class="auth-dialog-token-button"
            on:click={submitRefreshToken}
            disabled={loading || !refreshTokenInput.trim()}
          >
            {loading ? "Connecting..." : "Connect with token"}
          </button>
        </div>
      {/if}

      {#if responseMessage}
        <p class="auth-dialog-error">
          {responseMessage}
        </p>
      {/if}
    </div>
  </div>
{/if}

<style>
  .auth-dialog-overlay {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    background: var(--ace-overlay, rgba(14, 17, 30, 0.72));
  }

  .auth-dialog-card {
    width: 100%;
    max-width: 34rem;
    border-radius: 1.5rem;
    border: 1px solid var(--ace-border, rgba(226, 232, 240, 0.22));
    background: var(--ace-panel, #292d40);
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
    padding: 1.6rem;
  }

  .auth-dialog-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
  }

  .auth-dialog-eyebrow {
    margin: 0;
    color: var(--ace-primary, #f97316);
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    line-height: 1.2;
    text-transform: uppercase;
  }

  .auth-dialog-title {
    margin: 0.55rem 0 0;
    color: var(--ace-text, #f8fafc);
    font-size: 2rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.08;
  }

  .auth-dialog-close {
    appearance: none;
    border: 1px solid var(--ace-border, rgba(226, 232, 240, 0.22));
    border-radius: 9999px;
    background: var(--ace-button, #35364d);
    color: var(--ace-text-muted, #dbe3f0);
    cursor: pointer;
    font-size: 0.95rem;
    font-weight: 500;
    line-height: 1.2;
    padding: 0.65rem 1rem;
  }

  .auth-dialog-close:hover {
    border-color: var(--ace-border-strong, rgba(226, 232, 240, 0.4));
    color: var(--ace-text, #f8fafc);
    background: var(--ace-button-hover, #41445d);
  }

  .auth-dialog-copy {
    margin: 1rem 0 0;
    color: var(--ace-text-muted, #dbe3f0);
    font-size: 1rem;
    line-height: 1.6;
  }

  .auth-dialog-highlight {
    margin-top: 1.15rem;
    border: 1px solid var(--ace-primary-border, rgba(251, 146, 60, 0.45));
    border-radius: 1rem;
    background: var(--ace-primary-surface, rgba(249, 115, 22, 0.12));
    padding: 1rem 1.05rem;
  }

  .auth-dialog-highlight-title {
    margin: 0;
    color: var(--ace-primary-text, #fdba74);
    font-size: 0.95rem;
    font-weight: 700;
    line-height: 1.3;
  }

  .auth-dialog-highlight-copy {
    margin: 0.35rem 0 0;
    color: var(--ace-primary-text-muted, #fed7aa);
    font-size: 0.92rem;
    line-height: 1.5;
  }

  .auth-dialog-actions {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    margin-top: 1.2rem;
  }

  .auth-dialog-primary,
  .auth-dialog-secondary,
  .auth-dialog-token-button {
    appearance: none;
    width: 100%;
    min-height: 3.2rem;
    border-radius: 1rem;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.2;
    padding: 0.9rem 1.1rem;
  }

  .auth-dialog-primary {
    border: 1px solid var(--ace-primary-border, rgba(249, 115, 22, 0.4));
    background: var(--ace-primary, #f97316);
    color: var(--ace-primary-contrast, #ffffff);
  }

  .auth-dialog-secondary,
  .auth-dialog-token-button {
    border: 1px solid var(--ace-border, rgba(226, 232, 240, 0.22));
    background: var(--ace-button, #35364d);
    color: var(--ace-text, #f8fafc);
  }

  .auth-dialog-secondary:hover:enabled,
  .auth-dialog-token-button:hover:enabled {
    background: var(--ace-button-hover, #41445d);
    border-color: var(--ace-border-strong, rgba(226, 232, 240, 0.4));
  }

  .auth-dialog-token-box {
    margin-top: 1rem;
    border: 1px solid var(--ace-border-subtle, rgba(226, 232, 240, 0.14));
    border-radius: 1rem;
    background: var(--ace-surface, #25293a);
    padding: 1rem;
  }

  .auth-dialog-label {
    display: block;
    color: var(--ace-text, #f8fafc);
    font-size: 0.92rem;
    font-weight: 600;
    line-height: 1.3;
  }

  .auth-dialog-input {
    width: 100%;
    margin-top: 0.65rem;
    border: 1px solid var(--ace-border, rgba(226, 232, 240, 0.22));
    border-radius: 0.95rem;
    background: var(--ace-input, var(--ace-surface, #25293a));
    color: var(--ace-text, #f8fafc);
    font-size: 0.95rem;
    line-height: 1.2;
    outline: none;
    padding: 0.85rem 0.95rem;
    transition: border-color 150ms ease, box-shadow 150ms ease;
  }

  .auth-dialog-input::placeholder {
    color: var(--ace-text-muted, #dbe3f0);
  }

  .auth-dialog-input:focus {
    border-color: var(--ace-primary, #f97316);
  }

  .auth-dialog-token-button {
    margin-top: 0.8rem;
  }

  .auth-dialog-error {
    margin: 1rem 0 0;
    border: 1px solid var(--ace-danger-border, rgba(248, 113, 113, 0.45));
    border-radius: 1rem;
    background: var(--ace-danger-surface, rgba(127, 29, 29, 0.35));
    color: var(--ace-danger-text, #fecaca);
    font-size: 0.92rem;
    line-height: 1.5;
    padding: 0.9rem 1rem;
  }

  .auth-dialog-primary:disabled,
  .auth-dialog-secondary:disabled,
  .auth-dialog-token-button:disabled,
  .auth-dialog-close:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  @media (max-width: 640px) {
    .auth-dialog-card {
      padding: 1.25rem;
    }

    .auth-dialog-head {
      flex-direction: column;
    }

    .auth-dialog-title {
      font-size: 1.65rem;
    }
  }
</style>
